/**
 * GEKHA Quick Add sheet
 *
 * Product-card "Add to cart" buttons ([data-quick-add]) open a sheet that
 * slides in from the right on desktop and up from the bottom on mobile.
 * Product data comes from /products/{handle}.js on click, so cards carry no
 * variant markup. Adding uses the theme's own cart endpoint and then the
 * theme's cart drawer, mirroring ProductForm in main.js.
 *
 * Vanilla JS, one delegated listener; safe to load once globally.
 */
(function () {
  'use strict';

  if (window.__gekhaQuickAdd) return;
  window.__gekhaQuickAdd = true;

  var cache = {};
  var sheet = null;
  var opener = null;
  var state = null;

  function t(key, fallback) {
    return (window.theme && theme.strings && theme.strings[key]) || fallback;
  }

  function money(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function sized(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ── Sheet shell (built once) ───────────────────────────────── */
  function buildSheet() {
    var root = el('div', 'gk-qa');
    root.setAttribute('hidden', '');
    root.innerHTML =
      '<div class="gk-qa__overlay" data-qa-close></div>' +
      '<div class="gk-qa__panel" role="dialog" aria-modal="true" aria-labelledby="gk-qa-title" tabindex="-1">' +
        '<button type="button" class="gk-qa__close" data-qa-close aria-label="Close">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="gk-qa__body"></div>' +
      '</div>';
    document.body.appendChild(root);

    root.addEventListener('click', function (evt) {
      if (evt.target.closest('[data-qa-close]')) close();
    });
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && !root.hasAttribute('hidden')) close();
    });
    return root;
  }

  /* ── Rendering ──────────────────────────────────────────────── */
  function optionList(product) {
    return (product.options || []).map(function (opt, i) {
      if (typeof opt === 'string') {
        var seen = [];
        product.variants.forEach(function (v) {
          var val = v['option' + (i + 1)];
          if (seen.indexOf(val) === -1) seen.push(val);
        });
        return { name: opt, values: seen };
      }
      return { name: opt.name, values: opt.values };
    });
  }

  function findVariant() {
    return state.product.variants.filter(function (v) {
      return state.selected.every(function (val, i) {
        return v['option' + (i + 1)] === val;
      });
    })[0];
  }

  // A value is purchasable if some in-stock variant matches it together with
  // the other options currently selected.
  function valueAvailable(optIndex, value) {
    return state.product.variants.some(function (v) {
      if (!v.available || v['option' + (optIndex + 1)] !== value) return false;
      return state.selected.every(function (sel, i) {
        return i === optIndex || v['option' + (i + 1)] === sel;
      });
    });
  }

  function render() {
    var p = state.product;
    var body = sheet.querySelector('.gk-qa__body');
    body.textContent = '';

    var variant = findVariant();
    var price = variant ? variant.price : p.price;
    var compare = variant ? variant.compare_at_price : p.compare_at_price;

    var media = el('div', 'gk-qa__media');
    var imgSrc = (variant && variant.featured_image && variant.featured_image.src) || p.featured_image;
    if (imgSrc) {
      var img = document.createElement('img');
      img.src = sized(imgSrc, 800);
      img.alt = p.title;
      media.appendChild(img);
    }
    body.appendChild(media);

    var info = el('div', 'gk-qa__info');
    var title = el('h2', 'gk-qa__title', p.title);
    title.id = 'gk-qa-title';
    info.appendChild(title);

    var priceRow = el('div', 'gk-qa__price-row');
    priceRow.appendChild(el('span', 'gk-qa__price', money(price)));
    if (compare && compare > price) {
      priceRow.appendChild(el('span', 'gk-qa__compare', money(compare)));
      priceRow.appendChild(el('span', 'gk-qa__off', Math.round((compare - price) * 100 / compare) + '% off'));
    }
    info.appendChild(priceRow);

    var isDefaultOnly = p.variants.length === 1 && p.variants[0].title === 'Default Title';
    if (!isDefaultOnly) {
      optionList(p).forEach(function (opt, optIndex) {
        var group = el('div', 'gk-qa__option');
        var label = el('div', 'gk-qa__option-label');
        label.appendChild(el('span', 'gk-qa__option-name', opt.name));
        label.appendChild(el('span', 'gk-qa__option-value', state.selected[optIndex] || ''));
        group.appendChild(label);

        var chips = el('div', 'gk-qa__chips');
        chips.setAttribute('role', 'radiogroup');
        chips.setAttribute('aria-label', opt.name);
        opt.values.forEach(function (val) {
          var chip = el('button', 'gk-qa__chip', val);
          chip.type = 'button';
          chip.setAttribute('role', 'radio');
          var on = state.selected[optIndex] === val;
          chip.setAttribute('aria-checked', on ? 'true' : 'false');
          if (on) chip.classList.add('is-selected');
          if (!valueAvailable(optIndex, val)) chip.classList.add('is-soldout');
          chip.addEventListener('click', function () {
            state.selected[optIndex] = val;
            state.error = '';
            render();
            var again = sheet.querySelector('.gk-qa__chip.is-selected');
            if (again) again.focus();
          });
          chips.appendChild(chip);
        });
        group.appendChild(chips);
        info.appendChild(group);
      });
    }

    var err = el('p', 'gk-qa__error', state.error || '');
    err.setAttribute('role', 'alert');
    if (!state.error) err.hidden = true;
    info.appendChild(err);

    var actions = el('div', 'gk-qa__actions');
    var add = el('button', 'gk-qa__add');
    add.type = 'button';
    var unavailable = !variant || !variant.available;
    var addLabel = !variant ? t('noVariant', 'Unavailable') : (variant.available ? 'Add to cart' : t('noStock', 'Sold out'));
    if (variant && variant.available) {
      add.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 7h12l1 13H5L6 7z"/><path d="M9 7a3 3 0 0 1 6 0"/></svg>';
    }
    add.appendChild(el('span', null, state.busy ? 'Adding…' : addLabel));
    add.disabled = unavailable || state.busy;
    if (state.busy) add.classList.add('is-loading');
    add.addEventListener('click', submit);
    actions.appendChild(add);

    var full = el('a', 'gk-qa__details', 'View full details');
    full.href = '/products/' + p.handle;
    actions.appendChild(full);
    info.appendChild(actions);

    body.appendChild(info);
  }

  /* ── Add to cart ────────────────────────────────────────────── */
  async function submit() {
    var variant = findVariant();
    if (!variant || !variant.available || state.busy) return;

    state.busy = true;
    state.error = '';
    render();

    var targets = ['page-header', 'cart-drawer']
      .map(function (sel) { return document.querySelector(sel); })
      .filter(Boolean);

    var payload = {
      items: [{ id: variant.id, quantity: 1 }],
      sections: targets.map(function (n) { return n.dataset.sectionId; }).join(','),
      sections_url: window.location.pathname
    };

    try {
      var response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      var data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      var after = (theme.settings && theme.settings.afterAddToCart) || 'drawer';
      var openerEl = opener;
      close(true);

      if (document.querySelector('.template-cart')) {
        var cartForm = document.querySelector('cart-form');
        if (cartForm && cartForm.enableAjaxUpdate) cartForm.refresh();
      } else if (after === 'page') {
        window.location.href = theme.routes.cart;
        return;
      } else {
        if (data.sections && targets.length === Object.keys(data.sections).length) {
          targets.forEach(function (n) {
            if (typeof n.updateFromCartChange === 'function') {
              n.updateFromCartChange(data.sections[n.dataset.sectionId]);
            }
          });
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }
        var drawer = document.querySelector('.js-cart-drawer');
        if (after === 'drawer' && drawer && typeof drawer.open === 'function') {
          drawer.open(openerEl);
        }
      }

      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId: data.items ? data.items[0].variant_id : variant.id }
      }));
    } catch (e) {
      state.busy = false;
      state.error = e.message;
      render();
    }
  }

  /* ── Open / close ───────────────────────────────────────────── */
  async function open(btn) {
    var handle = btn.dataset.productHandle;
    if (!handle || btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');
    try {
      if (!cache[handle]) {
        var res = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' }
        });
        if (!res.ok) throw new Error(res.status);
        cache[handle] = await res.json();
      }
    } catch (e) {
      // Fall back to the product page if the data can't be fetched.
      window.location.href = '/products/' + handle;
      return;
    } finally {
      btn.classList.remove('is-loading');
    }

    var product = cache[handle];
    var first = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
    state = {
      product: product,
      selected: [first.option1, first.option2, first.option3].slice(0, product.options.length),
      busy: false,
      error: ''
    };
    opener = btn;

    sheet = sheet || buildSheet();
    render();
    sheet.removeAttribute('hidden');
    document.documentElement.classList.add('gk-qa-open');
    // Force a frame so the slide-in transition runs.
    requestAnimationFrame(function () {
      sheet.classList.add('is-open');
      var panel = sheet.querySelector('.gk-qa__panel');
      if (typeof trapFocus === 'function') trapFocus(panel, panel);
      else panel.focus();
    });
  }

  function close(skipFocus) {
    if (!sheet || sheet.hasAttribute('hidden')) return;
    sheet.classList.remove('is-open');
    document.documentElement.classList.remove('gk-qa-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(skipFocus === true ? null : opener);
    else if (skipFocus !== true && opener) opener.focus();

    var panel = sheet.querySelector('.gk-qa__panel');
    var done = function () { sheet.setAttribute('hidden', ''); };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
    } else {
      var finished = false;
      var finish = function () { if (!finished) { finished = true; if (!sheet.classList.contains('is-open')) done(); } };
      panel.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    }
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('[data-quick-add]');
    if (!btn) return;
    evt.preventDefault();
    open(btn);
  });
})();
